import { useState } from 'react';
import type {
  AuthAuditEntry,
  ClientSecretInfo,
  IssuedClientSecret,
  ServiceClient,
  Tenant,
} from '../../../identity/api';
import { Button, Chip, Dialog, Select, TextField } from '@nyce/admin-kit/ui';
import { activeTargetInfo, client } from '../../../identity/session';
import { fmtDate, sortedByName } from '../../../identity/format';
import {
  ActionsRow,
  Code,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  SortSelect,
  SortTh,
  TableScroller,
  useAsync,
  useSort,
} from '../../../components/common';
import { useParam } from '../../statistics/shared';
import { covered, tenantScopes } from './RolesTab';
import { ScopeSelect } from './ScopeSelect';
import { CopyRow } from './ScimConnection';

type Notify = { success: (t: string) => void; error: (t: string) => void };

/** AUTH keeps at most two active secrets per client (`Auth:ServiceClients:MaxActiveSecrets`, overlap rotation). */
export const MAX_ACTIVE_SECRETS = 2;

/** AUTH §10.9: a client name is a lowercase slug. */
const NAME_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

const STILL_VALID = 'Tokens already issued keep working until they expire (up to 15 min).';

const shellQuote = (s: string) => `'${s.replace(/'/g, `'\\''`)}'`;

/**
 * The `client_credentials` request (RFC 6749 §4.4) against this environment's AUTH: form-encoded, the client
 * authenticated with HTTP Basic. Reply `{access_token, token_type, expires_in, scope}`; no refresh token.
 */
export const tokenCurl = (authUrl: string, clientId: string, secret: string, scopes: string[]) =>
  [
    `curl -s -u ${shellQuote(`${clientId}:${secret}`)} \\`,
    `  -d grant_type=client_credentials \\`,
    ...(scopes.length ? [`  --data-urlencode ${shellQuote(`scope=${scopes.join(' ')}`)} \\`] : []),
    `  ${authUrl.replace(/\/+$/, '')}/api/auth/v1/token`,
  ].join('\n');

function ClientDialog({
  tenant,
  current,
  onSaved,
  onClose,
}: {
  tenant: Tenant;
  current?: ServiceClient;
  onSaved: (c: ServiceClient) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(current?.name ?? '');
  const [description, setDescription] = useState(current?.description ?? '');
  const [scopes, setScopes] = useState<string[]>(current?.scopes ?? []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ceiling = tenantScopes(tenant);
  const outside = scopes.filter((s) => !covered(ceiling, s));
  const slug = name.trim().toLowerCase();
  const nameOk = NAME_RE.test(slug);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { name: slug, description: description.trim() || null, scopes };
      const saved = current
        ? await client.tenants.clients.update(tenant.id, current.id, { ...body, status: current.status })
        : await client.tenants.clients.create(tenant.id, body);
      await onSaved(saved);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={current ? `Client ${current.name}` : 'New service client'}
      maxWidth={640}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={!nameOk}>
            {current ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form">
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={63}
          placeholder="wms-stark-ws1"
          hint="Lowercase letters, digits and dashes; unique in the tenant. The calling service or machine."
          error={name.trim() && !nameOk ? 'Use a lowercase slug, e.g. agent-dc1' : undefined}
          required
          autoFocus
          fieldClassName="nyk-span2"
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          fieldClassName="nyk-span2"
        />
        <ScopeSelect
          ceiling={ceiling}
          value={scopes}
          onChange={setScopes}
          known={tenant.roles.flatMap((r) => r.scopes)}
          placeholder="datacollector:write, boxcalc:calculate…"
          hint="What the client may ask for; a token gets requested ∩ these ∩ the tenant's scope claims."
        />
        {outside.length > 0 && (
          <div className="nyk-notice nyk-notice-warn nyk-span2" data-testid="client-outside-ceiling">
            Not covered by the tenant's claims, so not granted until the tenant gets them:{' '}
            {outside.map((s) => (
              <code key={s}>{s} </code>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}

const EXPIRY: { value: string; label: string }[] = [
  { value: '', label: 'Never expires' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '1 year' },
  { value: '730', label: '2 years' },
];

/** The new secret, shown exactly once, with what a service needs to use it. */
function IssuedSecret({ issued, scopes }: { issued: IssuedClientSecret; scopes: string[] }) {
  const authUrl = activeTargetInfo()?.authUrl ?? '';
  return (
    <div className="nyk-form" data-testid="issued-secret">
      <div className="nyk-notice nyk-notice-warn nyk-span2">
        Copy the secret now — only its hash is stored and it cannot be shown again.
        {issued.expires_at ? ` It expires ${fmtDate(issued.expires_at)}.` : ' It does not expire.'}
      </div>
      <div className="nyk-span2">
        <CopyRow label="client_id" value={issued.client_id} testId="client-id" />
      </div>
      <div className="nyk-span2">
        <CopyRow label="client_secret" value={issued.client_secret} testId="client-secret" />
      </div>
      {authUrl && (
        <div className="nyk-span2">
          <CopyRow
            label="Token request (client_credentials)"
            value={tokenCurl(authUrl, issued.client_id, issued.client_secret, scopes)}
            testId="client-curl"
          />
          <span className="nyk-ui-hint">
            Reply <code>{'{access_token, token_type, expires_in, scope}'}</code>, no refresh token: ask again
            before it expires. A scope nothing covers is <code>invalid_scope</code>.
          </span>
        </div>
      )}
    </div>
  );
}

function SecretsDialog({
  tenant,
  sc,
  canWrite,
  notify,
  onChanged,
  onClose,
}: {
  tenant: Tenant;
  sc: ServiceClient;
  canWrite: boolean;
  notify: Notify;
  onChanged: () => Promise<void>;
  onClose: () => void;
}) {
  const secrets = useAsync(() => client.tenants.clients.secrets.list(tenant.id, sc.id), [tenant.id, sc.id]);
  const [expiry, setExpiry] = useState('');
  const [issued, setIssued] = useState<IssuedClientSecret | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rows = [...(secrets.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const active = rows.filter((s) => s.active).length;
  const full = active >= MAX_ACTIVE_SECRETS;

  const issue = async () => {
    setBusy(true);
    setError(null);
    try {
      setIssued(
        await client.tenants.clients.secrets.issue(
          tenant.id,
          sc.id,
          expiry ? { expiresInDays: Number(expiry) } : {}
        )
      );
      await secrets.reload();
      await onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const revoke = async (s: ClientSecretInfo) => {
    try {
      await client.tenants.clients.secrets.revoke(tenant.id, sc.id, s.id);
      await secrets.reload();
      await onChanged();
      notify.success(`Secret ${s.id} revoked`);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Secrets of ${sc.name}`}
      maxWidth={760}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {issued ? 'I have stored the secret' : 'Close'}
        </Button>
      }
    >
      <ErrorBox error={error ?? secrets.error} />
      {issued ? (
        <IssuedSecret issued={issued} scopes={sc.scopes} />
      ) : (
        <>
          <p className="nyk-card-intro">
            client_id <Code>{sc.clientId ?? sc.id}</Code> · at most {MAX_ACTIVE_SECRETS} active secrets: issue
            the new one, roll it out, then revoke the old one.
          </p>
          {canWrite && (
            <div className="nyk-actions" style={{ marginBottom: 12 }}>
              <Select
                aria-label="Expires in"
                value={expiry}
                onChange={setExpiry}
                options={EXPIRY}
                disabled={full}
              />
              <Button size="sm" onClick={() => void issue()} loading={busy} disabled={full}>
                Issue secret
              </Button>
              {full && (
                <span className="nyk-ui-hint" data-testid="secrets-full">
                  {MAX_ACTIVE_SECRETS} active secrets: revoke one first.
                </span>
              )}
            </div>
          )}
        </>
      )}
      <Loading loading={secrets.loading} />
      <TableScroller>
        <table className="nyk-ui-table nyk-cards" data-testid="secrets-table">
          <thead>
            <tr>
              <th>Secret</th>
              <th>Status</th>
              <th>Created</th>
              <th>Expires</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} data-secret={s.id}>
                <td className="nyk-card-title">
                  <Code>{s.id}</Code>
                  {s.createdBy && <div className="nyk-ui-hint">by {s.createdBy}</div>}
                </td>
                <td data-label="Status">
                  <Chip size="sm" selected={s.active}>
                    {s.revokedAt ? 'revoked' : s.active ? 'active' : 'expired'}
                  </Chip>
                </td>
                <td data-label="Created">{fmtDate(s.createdAt)}</td>
                <td data-label="Expires">{s.expiresAt ? fmtDate(s.expiresAt) : 'never'}</td>
                <td data-label="Last used">{fmtDate(s.lastUsedAt)}</td>
                <td className="nyk-row-actions">
                  {canWrite && s.active && (
                    <ConfirmButton
                      size="sm"
                      variant="danger"
                      message={`Revoke secret ${s.id} of ${sc.name}? It gets no new tokens; the other active secret keeps working. ${STILL_VALID}`}
                      onConfirm={() => void revoke(s)}
                    >
                      Revoke
                    </ConfirmButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroller>
      {!secrets.loading && !rows.length && (
        <EmptyState title="No secrets." hint="Issue one to let the client request tokens." />
      )}
    </Dialog>
  );
}

const CHANGE_LABELS: Record<string, string> = {
  'client.create': 'created',
  'client.update': 'updated',
  'client.delete': 'deleted',
  'client_secret.issue': 'secret issued',
  'client_secret.revoke': 'secret revoked',
};

/** Last client and secret changes of the tenant from AUTH's admin audit (read-only). */
function ClientChanges({
  tenant,
  clients,
  round,
}: {
  tenant: Tenant;
  clients: ServiceClient[];
  round: number;
}) {
  const audit = useAsync(
    () => client.audit({ tenantId: tenant.id, limit: 100 }),

    [tenant.id, round]
  );
  const rows = (audit.data ?? [])
    .filter((e: AuthAuditEntry) => e.action.startsWith('client.') || e.action.startsWith('client_secret.'))
    .slice(0, 10);
  const nameOf = (e: AuthAuditEntry) => {
    const id = e.action.startsWith('client_secret.') ? String(e.details?.clientId ?? '') : e.targetId;
    const name =
      clients.find((c) => c.id === id)?.name ?? (e.details?.name as string | undefined) ?? (id || e.targetId);
    return e.action.startsWith('client_secret.') ? `${name} · ${e.targetId}` : name;
  };
  return (
    <section className="nyk-card" data-testid="client-changes">
      <div className="nyk-card-head">
        <h2>Changes</h2>
        <span className="nyk-ui-hint">AUTH audit · clients and secrets · newest first</span>
      </div>
      {audit.error && <p className="nyk-ui-hint">Audit not available: {audit.error}</p>}
      <Loading loading={audit.loading && !audit.data} />
      {rows.length > 0 && (
        <ul className="oph-attention">
          {rows.map((e) => (
            <li key={e.id} data-change={e.action}>
              <span className="nyk-ui-hint">{fmtDate(e.at)}</span>
              <span className="oph-attention-name">
                {nameOf(e)} <span className="nyk-ui-hint">{CHANGE_LABELS[e.action] ?? e.action}</span>
              </span>
              <span className="oph-attention-detail">by {e.actor}</span>
            </li>
          ))}
        </ul>
      )}
      {audit.data && !rows.length && <p className="nyk-ui-hint">No client changes recorded.</p>}
    </section>
  );
}

/**
 * Service clients (AUTH §10.9): machine identities of this tenant that obtain short-lived JWTs with
 * `grant_type=client_credentials`. Scopes are capped by the tenant's scope claims; each client may hold two active
 * secrets so a rotation never interrupts the caller. Disabled = paused; delete is hard.
 */
export function ClientsTab({
  tenant,
  canWrite,
  notify,
}: {
  tenant: Tenant;
  canWrite: boolean;
  notify: Notify;
}) {
  const list = useAsync(() => client.tenants.clients.list(tenant.id), [tenant.id]);
  const [dialog, setDialog] = useState<{ current?: ServiceClient } | null>(null);
  const [secretsOf, setSecretsOf] = useState<ServiceClient | null>(null);
  const [warnings, setWarnings] = useState<{ name: string; scopes: string[] } | null>(null);
  const [round, setRound] = useState(0);
  const ceiling = tenantScopes(tenant);
  const sort = useSort(
    sortedByName(list.data ?? [], (c) => c.name),
    {
      name: { label: 'Client', value: (c) => c.name },
      status: { label: 'Status', value: (c) => c.status },
      used: { label: 'Last used', value: (c) => c.lastUsedAt, desc: true },
      secrets: { label: 'Secrets', value: (c) => c.activeSecrets, desc: true },
    },
    useParam('csort', '')
  );
  const clients = sort.rows;
  const changed = async () => {
    await list.reload();
    setRound((r) => r + 1);
  };

  const setStatus = async (c: ServiceClient, status: ServiceClient['status']) => {
    try {
      await client.tenants.clients.update(tenant.id, c.id, {
        name: c.name,
        description: c.description ?? null,
        scopes: c.scopes,
        status,
      });
      await changed();
      notify.success(`${status === 'Active' ? 'Enabled' : 'Disabled'} ${c.name}`);
    } catch (e) {
      notify.error((e as Error).message);
    }
  };
  const remove = async (c: ServiceClient) => {
    try {
      await client.tenants.clients.remove(tenant.id, c.id);
      await changed();
      notify.success(`Deleted client ${c.name}`);
    } catch (e) {
      notify.error((e as Error).message);
    }
  };

  return (
    <>
      <div className="nyk-card">
        <div className="nyk-card-head">
          <h2>Service clients</h2>
          {canWrite && (
            <Button size="sm" onClick={() => setDialog({})}>
              New client
            </Button>
          )}
        </div>
        <p className="nyk-card-intro">
          Services and machines of this installation (WorkServers, agents) request 15-minute tokens with{' '}
          <code>grant_type=client_credentials</code> at <code>/api/auth/v1/token</code>. Their scopes are
          capped by the tenant's scope claims; only secret hashes are stored.
        </p>
        {clients.length > 0 && !ceiling.length && (
          <div className="nyk-notice nyk-notice-warn">
            The tenant has no scope claims, so no client receives any scope. Add scopes under Claims first.
          </div>
        )}
        {warnings && (
          <div className="nyk-notice nyk-notice-warn" data-testid="client-warnings">
            {warnings.name}: stored, but not granted until the tenant holds{' '}
            {warnings.scopes.map((s) => (
              <code key={s}>{s} </code>
            ))}
          </div>
        )}
        <ErrorBox error={list.error} />
        <Loading loading={list.loading && !list.data} />
        <SortSelect sort={sort} />
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="clients-table">
            <thead>
              <tr>
                <SortTh sort={sort} by="name" />
                <th>Scopes</th>
                <SortTh sort={sort} by="status" />
                <SortTh sort={sort} by="used" />
                <SortTh sort={sort} by="secrets" />
              </tr>
            </thead>
            {clients.map((c) => (
              <tbody key={c.id} className="nyk-row-group" data-client={c.id}>
                <tr>
                  <td className="nyk-card-title">
                    <b>{c.name}</b>
                    {c.description && <div className="nyk-ui-hint">{c.description}</div>}
                    <div>
                      <Code title="client_id">{c.clientId ?? c.id}</Code>
                    </div>
                  </td>
                  <td data-label="Scopes">
                    <div className="nyk-inline">
                      {c.scopes.map((s) => (
                        <Chip
                          key={s}
                          size="sm"
                          selected={covered(ceiling, s)}
                          title={covered(ceiling, s) ? undefined : 'Outside the tenant claims: not granted'}
                        >
                          {s}
                        </Chip>
                      ))}
                      {!c.scopes.length && <span className="nyk-ui-hint">—</span>}
                    </div>
                  </td>
                  <td data-label="Status">
                    <Chip size="sm" className={c.status === 'Active' ? 'nyk-tone-green' : 'nyk-tone-grey'}>
                      {c.status === 'Active' ? 'active' : 'disabled'}
                    </Chip>
                  </td>
                  <td data-label="Last used">{fmtDate(c.lastUsedAt)}</td>
                  <td data-label="Secrets">
                    {c.activeSecrets} / {MAX_ACTIVE_SECRETS} active
                  </td>
                </tr>
                <ActionsRow colSpan={5}>
                  <Button size="sm" variant="ghost" onClick={() => setSecretsOf(c)}>
                    Secrets
                  </Button>
                  {canWrite && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setDialog({ current: c })}>
                        Edit
                      </Button>
                      {c.status === 'Active' ? (
                        <ConfirmButton
                          size="sm"
                          variant="ghost"
                          message={`Disable ${c.name}? It gets no new tokens until enabled again; its secrets are kept. ${STILL_VALID}`}
                          onConfirm={() => void setStatus(c, 'Disabled')}
                        >
                          Disable
                        </ConfirmButton>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => void setStatus(c, 'Active')}>
                          Enable
                        </Button>
                      )}
                      <ConfirmButton
                        size="sm"
                        variant="danger"
                        message={`Delete client ${c.name}? This is a hard delete including all its secrets and cannot be undone — to pause it, disable it instead. ${STILL_VALID}`}
                        onConfirm={() => void remove(c)}
                      >
                        Delete
                      </ConfirmButton>
                    </>
                  )}
                </ActionsRow>
              </tbody>
            ))}
          </table>
        </TableScroller>
        {!list.loading && !clients.length && !list.error && (
          <EmptyState
            title="No service clients."
            hint="Create one per calling service or machine that should authenticate with AUTH tokens."
          />
        )}
      </div>
      <ClientChanges tenant={tenant} clients={clients} round={round} />
      {dialog && (
        <ClientDialog
          tenant={tenant}
          current={dialog.current}
          onSaved={async (c) => {
            await changed();
            setWarnings(c.warnings?.length ? { name: c.name, scopes: c.warnings } : null);
            notify.success(`${dialog.current ? 'Saved' : 'Created'} client ${c.name}`);
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {secretsOf && (
        <SecretsDialog
          tenant={tenant}
          sc={secretsOf}
          canWrite={canWrite}
          notify={notify}
          onChanged={changed}
          onClose={() => setSecretsOf(null)}
        />
      )}
    </>
  );
}
